import {
  createFileRoute,
  Outlet,
  useChildMatches,
} from '@tanstack/react-router';
import { Preferences } from '@/pages/preferences';
import { commonSearchSchema } from '@/routes/-shared/commonSearchSchema';
import { RoleGuard } from '@/components/molecules/role-guard';
import { usePermissions } from '@/hooks/use-permissions';
import { canOpenSettingsPage } from '@/pages/preferences/lib/settings-access';
import { useSettingsNavContext } from '@/pages/preferences/hooks/use-settings-nav-context';
import { useTenant } from '@/lib/auth/tenant-context';
import { useRevisoCallback } from '@/pages/integrations/sections/reviso';
import z from '@/lib/zod';
import { TenantRole } from '@repo/database/browser';

export const settingsSearchSchema = commonSearchSchema.extend({
  tab: z.string().nullish(),
  subTab: z.string().nullish(),
  expandedCategories: z.string().nullish(),
  userTab: z.string().nullish(),
  apiTab: z.enum(['keys', 'webhooks', 'docs']).nullish(),
  bulkMode: z.enum(['category', 'movementGroups', 'documentGroups']).nullish(),
  // categoryParentId kept as query param for subcategory creation context
  categoryParentId: z.string().nullish(),
  // Users tab filter
  role: z.nativeEnum(TenantRole).nullish().catch(undefined),
  // Ritorno OAuth Reviso: Reviso rimanda a /settings?reviso_state=…&token=…
  // (un solo parametro nostro sopravvive a RequestAccess, `token` lo appende
  // Reviso). Consumati e rimossi da `useRevisoCallback`.
  reviso_state: z.string().nullish(),
  token: z.string().nullish(),
});

export type SettingsSearchSchema = z.infer<typeof settingsSearchSchema>;

const SETTINGS_INDEX_ROUTE_ID = '/protected/_layout/settings/_layout/';

export const Route = createFileRoute('/protected/_layout/settings/_layout')({
  validateSearch: settingsSearchSchema,
  component: SettingsLayout,
});

function SettingsLayout() {
  const permissions = usePermissions();
  const { selectedTenant } = useTenant();
  const { isSecurityPageAvailable } = useSettingsNavContext();
  const { tab } = Route.useSearch();
  const hasChildRoute = useChildMatches({
    select: (matches) =>
      matches.some((match) => match.routeId !== SETTINGS_INDEX_ROUTE_ID),
  });

  // Il ritorno OAuth di Reviso atterra su /settings (UI canonica delle
  // integrazioni): il callback va montato a livello di layout, non nella tab,
  // perché il tab attivo al ritorno potrebbe non essere "integrazioni".
  useRevisoCallback(selectedTenant?.id ?? '');

  return (
    <RoleGuard
      hasAccess={canOpenSettingsPage(permissions, tab, {
        hasChildRoute,
        isSecurityPageAvailable,
      })}
      isLoading={permissions.isLoading}
    >
      <Preferences />
      <Outlet />
    </RoleGuard>
  );
}
